import { useState } from 'react';
import FolderIcon from '@heroicons/react/24/outline/FolderIcon';
import FolderIconFull from '@heroicons/react/24/solid/FolderIcon';
import type { Folder, File, FileItem } from '@types';
import { Button, Menu } from 'react-daisyui';

import FileComponent from '@/components/FileBrowser/FileComponent';
import { FilePath } from '@/components/FileBrowser/FileUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCloudUpload,
  faLink,
  faEye,
  faPaperPlane,
  faQuestion,
} from '@fortawesome/free-solid-svg-icons';
import TagsAndUsersDropDownMenu from '@/components/FileBrowser/TagsAndUsersDropDownMenu';
import { IUserContext, useUserContext } from '@/context/UserContext';
import ShareFolder from '@/components/FileBrowser/ShareFolder';
import { slugify } from '@/utils/string';
import RequestPermission from '@/components/FileBrowser/RequestPermission';
import { clipLastFolder } from './utils';

import {
  FileSelectAction,
  FolderSelectAction,
  FileSearchItem,
  isFileSearchItem,
} from './types';

const iconClasses = 'w-6 mr-2 flex-shrink-0 text-accent';
export function FolderOrFileComponent({
  item,
  onFileSelect,
  onFolderSelect,
  showLocation = true,
  openUpload,
  currentFolderPath,
  disabledClick,
  hideRequestPermissions,
}: {
  item: FileItem | FileSearchItem;
  onFileSelect?: FileSelectAction;
  onFolderSelect?: FolderSelectAction;
  showLocation?: boolean;
  openUpload?: (folder: Folder) => void;
  currentFolderPath?: string;
  disabledClick?: boolean;
  hideRequestPermissions?: boolean;
}) {
  return item.type === 'folder' ? (
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    <FolderComponent
      folder={item as Folder}
      onFileSelect={onFileSelect}
      onFolderSelect={onFolderSelect}
      openUpload={openUpload}
      currentFolderPath={currentFolderPath as string}
      hideRequestPermissions={hideRequestPermissions}
    />
  ) : (
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    <FileComponent
      file={item as File}
      showLocation={showLocation}
      disabledClick={disabledClick}
      hideRequestPermissions={hideRequestPermissions}
    />
  );
}

function FolderComponent({
  folder,
  onFolderSelect,
  openUpload,
  currentFolderPath,
  hideRequestPermissions,
}: {
  folder: Folder | FileSearchItem;
  // eslint-disable-next-line react/no-unused-prop-types
  onFileSelect?: FileSelectAction;
  onFolderSelect?: FolderSelectAction;
  openUpload?: (folder: Folder) => void;
  currentFolderPath: string;
  hideRequestPermissions?: boolean;
}) {
  const { auth, impersonatingAccountId }: IUserContext = useUserContext();
  const [showShareFolder, setShowShareFolder] = useState(false);
  const [showAskFolderAccess, setShowAskFolderAcces] = useState(false);

  const openFolder = (
    url: string,
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.stopPropagation();
    window.open(url);
  };

  const showFileUploadModal = (
    folderToShow: Folder,
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.stopPropagation();
    if (openUpload) {
      openUpload(folderToShow);
    }
  };

  const toggleShareFolderModal = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.stopPropagation();
    setShowShareFolder(!showShareFolder);
    return onFolderSelect ? () => onFolderSelect(folder) : undefined;
  };

  const toggleAskFolderAccessModal = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.stopPropagation();
    setShowAskFolderAcces(!showAskFolderAccess);
    return onFolderSelect ? () => onFolderSelect(folder) : undefined;
  };

  const hasPermissions =
    (folder.permissions?.filter((p) => p.fileId === folder.id)?.length ?? 0) >
    0;

  const blockedForThirdParty = auth?.isInvestor && !folder.userHasPermission;

  return (
    <Menu.Item
      onClick={
        onFolderSelect && !blockedForThirdParty
          ? () => onFolderSelect(folder)
          : undefined
      }
      data-testid='folder-item'
    >
      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
      <a
        /* eslint-disable-next-line jsx-a11y/anchor-is-valid */
        className={`${
          blockedForThirdParty ? 'pointer-events-none cursor-not-allowed' : ''
        }`}
      >
        {(folder as Folder)?.files?.length === 0 && (
          <FolderIcon
            className={`${iconClasses} ${
              blockedForThirdParty ? 'opacity-50' : ''
            }`}
            data-testid='folder-empty'
          />
        )}
        {(folder as Folder)?.files?.length > 0 && (
          <FolderIconFull
            className={`${iconClasses} ${
              blockedForThirdParty ? 'opacity-50' : ''
            }`}
            data-testid='folder-content'
          />
        )}
        <span className={`${blockedForThirdParty ? 'opacity-50' : ''}`}>
          {folder.name}
        </span>
        {isFileSearchItem(folder) ? (
          // eslint-disable-next-line @typescript-eslint/no-use-before-define
          <FilePath path={clipLastFolder(folder.path)} />
        ) : null}
        <div className='right-4 absolute flex justify-end w-1/2'>
          {auth?.isClient || (auth?.isAdmin && impersonatingAccountId) ? (
            <>
              <Button
                onClick={(e) => {
                  if (!blockedForThirdParty) {
                    toggleShareFolderModal(e);
                  } else {
                    e.stopPropagation();
                  }
                }}
                size='xs'
                className={`${
                  blockedForThirdParty ? 'hidden' : ''
                }              
              btn-accent btn-outline ml-2`}
              >
                <FontAwesomeIcon
                  data-testid='share-button'
                  icon={faPaperPlane}
                  size='lg'
                  title='Share Folder'
                />
              </Button>
              <ShareFolder
                auth={auth}
                isOpen={showShareFolder}
                clientId={impersonatingAccountId || auth?.clients?.[0]?.id}
                onClose={() => setShowShareFolder(false)}
                currentFolderPath={`${currentFolderPath}/${slugify(
                  folder.name
                )}`}
              />
            </>
          ) : null}
          {(auth?.isClient || impersonatingAccountId) &&
          !auth?.isInvestor &&
          folder?.webUrl &&
          hasPermissions ? (
            <Button
              size='xs'
              data-testid='user-access-button'
              className={`${blockedForThirdParty ? 'hidden' : ''}
            menu-hover border-none group cursor-pointer ml-2`}
            >
              <FontAwesomeIcon
                data-testid='users-view-button'
                icon={faEye}
                size='lg'
                className={
                  folder?.permissions !== null && hasPermissions
                    ? 'text-accent'
                    : 'text-gray-500'
                }
              />
              <div
                className={`invisible font-thin absolute -bottom-7 top-5 w-60 z-10
                h-max bg-zinc-900 rounded group-hover:visible ${
                  auth?.isClient ? 'mr-40' : 'mr-20'
                }`}
              >
                <TagsAndUsersDropDownMenu folder={folder} />
              </div>
            </Button>
          ) : null}
          {folder?.webUrl ? (
            <>
              {!auth?.isInvestor ? (
                <Button
                  onClick={(e) => showFileUploadModal(folder as Folder, e)}
                  size='xs'
                  data-testid='file-upload-button'
                  variant='outline'
                  className={`btn-accent ml-2 ${
                    blockedForThirdParty ? 'hidden' : ''
                  }`}
                >
                  <FontAwesomeIcon
                    data-testid='upload-button'
                    icon={faCloudUpload}
                    size='lg'
                    id={`${folder.id}_upload_button`}
                    title='Upload Files'
                    aria-labelledby={`${folder.id}_upload_button`}
                  />
                </Button>
              ) : null}
              {!auth?.isClient ? (
                <Button
                  onClick={(e) => {
                    if (!blockedForThirdParty) {
                      openFolder(folder.webUrl as string, e);
                    } else {
                      e.stopPropagation();
                    }
                  }}
                  size='xs'
                  className={`btn-accent ml-2 ${
                    blockedForThirdParty ? 'hidden' : ''
                  }`}
                >
                  <FontAwesomeIcon
                    data-testid='opendrive-button'
                    icon={faLink}
                    size='lg'
                    title='Open OneDrive Folder'
                  />
                </Button>
              ) : null}
              {auth?.isInvestor &&
              blockedForThirdParty &&
              !hideRequestPermissions ? (
                <>
                  <Button
                    onClick={(e) => {
                      toggleAskFolderAccessModal(e);
                    }}
                    size='xs'
                    data-testid='request-permission'
                    className='btn-accent ml-2 pointer-events-auto'
                  >
                    <FontAwesomeIcon
                      data-testid='opendrive-button'
                      icon={faQuestion}
                      size='lg'
                      title='Open OneDrive Folder'
                    />
                  </Button>
                  <RequestPermission
                    auth={auth}
                    isOpen={showAskFolderAccess}
                    clientId={impersonatingAccountId || auth?.clients?.[0]?.id}
                    onClose={() => setShowAskFolderAcces(false)}
                    fileItem={folder}
                  />
                </>
              ) : null}
            </>
          ) : null}
        </div>
      </a>
    </Menu.Item>
  );
}

export default FolderComponent;
